/**
 * abandonedJourneyApi.js
 *
 * Service layer for Abandoned Journey Dashboard.
 * - fetchCSRUsers()   → real API: GET admin/v1/users/1  (filtered to CSR role)
 * - updateFollowUp()  → simulated API (returns resolved promise with 300ms delay)
 *                       Replace Promise mock with real axios.patch() when backend is ready.
 * - assignSession()   → simulated API (same pattern)
 */

import axios from 'axios';
import { Config } from '../../Common/Config';

const getHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

// ── Real API ──────────────────────────────────────────────────────────────────

/**
 * Fetch all users with role 'CSR' from the real backend.
 * Iterates pages until all CSR users are collected.
 * Returns: [{ user_id, name, surname, email, role }]
 */
export const fetchCSRUsers = async () => {
  let page = 1;
  let allCSRs = [];

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const response = await axios.get(`${Config.url}users/${page}`, {
      headers: getHeaders(),
    });

    const users = response?.data?.data?.users ?? [];
    if (!users.length) break;

    const csrs = users.filter(
      u => u.role && u.role.toLowerCase() === 'csr'
    );
    allCSRs = [...allCSRs, ...csrs];

    const total = response?.data?.data?.total ?? 0;
    const perPage = response?.data?.data?.per_page ?? users.length;
    if (page * perPage >= total) break;
    page++;
  }

  return allCSRs.map(u => ({
    user_id: u.user_id,
    name:    u.name    || '',
    surname: u.surname || '',
    email:   u.email   || '',
    role:    u.role    || 'CSR',
  }));
};

// ── Simulated API (mockable — swap for real axios calls when backend is ready) ──

const API_DELAY = 350; // ms — feels like a real network round-trip

/**
 * PATCH /admin/v1/abandoned-sessions/{session_id}/follow-up
 * Updates follow_up_status.
 * TODO: replace mock with:
 *   return axios.patch(`${Config.url}abandoned-sessions/${sessionId}/follow-up`,
 *     { follow_up_status: status }, { headers: getHeaders() }
 *   ).then(r => r.data);
 */
export const updateFollowUpStatus = async (sessionId, status) => {
  await new Promise(r => setTimeout(r, API_DELAY));
  // Simulate success response shape (mirrors planned backend)
  return {
    success: true,
    data: { session_id: sessionId, follow_up_status: status },
  };
};

/**
 * PATCH /admin/v1/abandoned-sessions/{session_id}/follow-up
 * Assigns a CSR to the session.
 * TODO: replace mock with real axios call (same endpoint as above).
 */
export const assignSessionToCSR = async (sessionId, csrUserId) => {
  await new Promise(r => setTimeout(r, API_DELAY));
  return {
    success: true,
    data: { session_id: sessionId, assigned_csr_id: csrUserId },
  };
};
